import { ChevronDownIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { Spinner } from './Animations';
import { cn } from './cn';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './DropdownMenu';

export type SplitButtonMenuItem = {
  key: string;
  text: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
};

export type SplitButtonProps = {
  icon?: React.ReactNode;
  text?: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Disables only the primary action; the menu trigger stays usable (unlike `disabled`). */
  primaryDisabled?: boolean;
  menuItems?: SplitButtonMenuItem[];
  /** Accessible name for the chevron menu trigger (defaults to "{text} options" or "More options"). */
  menuTriggerAriaLabel?: string;
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  loading?: boolean;
  className?: string;
  'data-test'?: string;
};

const variantClasses: Record<string, { main: string; divider: string; menu: string }> = {
  default: {
    main: 'bg-surface border border-border text-text-primary hover:bg-surface-hover',
    divider: 'bg-border',
    menu: 'bg-surface border border-border border-l-0 text-text-secondary hover:bg-surface-hover',
  },
  primary: {
    main: 'bg-primary-500 border border-primary-500 text-text-on-accent hover:bg-primary-600',
    divider: 'bg-primary-600/30',
    menu: 'bg-primary-500 border border-primary-500 border-l-0 text-text-on-accent hover:bg-primary-600',
  },
  danger: {
    main: 'bg-status-danger-emphasis border border-status-danger-emphasis text-text-on-danger hover:bg-danger-600',
    divider: 'bg-danger-600/30',
    menu: 'bg-status-danger-emphasis border border-status-danger-emphasis border-l-0 text-text-on-danger hover:bg-danger-600',
  },
  ghost: {
    main: 'bg-transparent border border-transparent text-text-secondary hover:bg-surface-hover',
    divider: 'bg-border',
    menu: 'bg-transparent border border-transparent text-text-muted hover:bg-surface-hover',
  },
};

export const SplitButton = React.forwardRef<HTMLDivElement, SplitButtonProps>(
  (
    {
      icon,
      text,
      onClick,
      disabled,
      primaryDisabled,
      menuItems,
      menuTriggerAriaLabel,
      variant = 'default',
      loading,
      className,
      'data-test': dataTest,
    },
    ref
  ) => {
    const classes = variantClasses[variant] || variantClasses.default;
    const hasMenu = menuItems && menuItems.length > 0;
    const menuTriggerLabel = menuTriggerAriaLabel ?? (text ? `${text} options` : 'More options');

    return (
      <div className={cn('relative inline-flex items-center', className)} data-test={dataTest} ref={ref}>
        <button
          onClick={onClick}
          disabled={disabled || primaryDisabled || loading}
          className={cn(
            'inline-flex h-9 items-center justify-center gap-1.5 px-4 text-sm font-medium transition-colors duration-200 cursor-pointer',
            hasMenu ? 'rounded-l-md' : 'rounded-md',
            classes.main,
            (disabled || primaryDisabled) && 'opacity-50 cursor-not-allowed',
            // Loading is a status, not a rejection: the spinner communicates it, so no
            // disabled fade or not-allowed cursor — just no interaction.
            loading && !disabled && !primaryDisabled && 'pointer-events-none'
          )}
        >
          {loading ? (
            <Spinner className="h-4 w-4" viewBox="0 0 24 24" fill="none" transition={{ duration: 1 }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </Spinner>
          ) : (
            icon
          )}
          {text && <span>{text}</span>}
        </button>
        {hasMenu && (
          <>
            <div className={cn('h-9 w-px', classes.divider)} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-test="more-actions-button"
                  aria-label={menuTriggerLabel}
                  // Menu actions stay available while the primary action is loading
                  // (e.g. a Starting deployment can still be edited or deleted).
                  disabled={disabled}
                  className={cn(
                    'inline-flex h-9 w-9 items-center justify-center rounded-r-md transition-colors duration-200 cursor-pointer',
                    classes.menu,
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <ChevronDownIcon className="h-4 w-4" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {menuItems.map((item) => (
                  <DropdownMenuItem
                    key={item.key}
                    onClick={item.onClick}
                    disabled={item.disabled}
                    className={item.className}
                  >
                    {item.icon}
                    <span>{item.text}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    );
  }
);
